import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, ADMIN_API_URL } from "./adminApi";
import { MOCK } from "./mock";

// Fetches a list resource from the external API via the graceful-fallback
// wrapper. Behaviour:
//   - Live-first: every fetch attempts the real API and only falls back to the
//     matching mock fixture when the API is unreachable (flags `mocked=true`).
//   - Live Data Cutover: while running on mock data, it silently re-checks the
//     API on an interval and swaps to live data the moment the API responds
//     (no spinner, no user action needed). Polling stops once live.
export function useResource(path, mockKey, { pollMs = 20000 } = {}) {
  const fallback = MOCK[mockKey];
  const [data, setData] = useState(Array.isArray(fallback) ? [] : fallback);
  const [mocked, setMocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      const res = await apiGet(path, fallback);
      let payload = res.data;
      // External APIs sometimes wrap arrays as { data: [...] } or { items: [...] }.
      if (payload && !Array.isArray(payload) && Array.isArray(fallback)) {
        payload = payload[mockKey] || payload.data || payload.items || fallback;
      }
      if (!mountedRef.current) return res;
      setData(payload);
      setMocked(res.mocked);
      if (!silent) setLoading(false);
      return res;
    },
    [path, mockKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const reload = useCallback(async () => {
    setRefreshing(true);
    await load(true);
    if (mountedRef.current) setRefreshing(false);
  }, [load]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Live Data Cutover: silently poll for the live API while on mock data.
  useEffect(() => {
    if (!mocked || !ADMIN_API_URL) return undefined; // already live, or nothing to poll
    const id = setInterval(() => {
      load(true);
    }, pollMs);
    return () => clearInterval(id);
  }, [mocked, load, pollMs]);

  return { data, mocked, loading, refreshing, reload };
}
